import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useBooking } from "../BookingContext";
import { publicCarsApi as carsApi, publicLocationsApi as locationsApi, CONTACT } from "../../api";
import { locations as damsLocationsApi } from "../damsApi";
import { DepositCard, MileagePolicyCard } from "../components/DepositPolicy";
import { ensureMinDays } from "../locationUtils";
import { ArrowLeft, Users, Briefcase, Fuel, Settings2, MapPin, Star, Check, Shield, Calendar, ChevronLeft, ChevronRight, Loader2, Phone, Mail, MessageCircle } from "lucide-react";

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { pickupLocation, pickupDate, dropoffDate, days, today, update } = useBooking();
  const [car, setCar] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationPolicy, setLocationPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([carsApi.get(id).catch(() => null), locationsApi.list().catch(() => [])])
      .then(([c, l]) => { setCar(c); setLocations(l); })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const policyLocationName = car?.pickupLocation || pickupLocation;
    if (!policyLocationName) return;
    let cancelled = false;
    damsLocationsApi
      .taxByName(policyLocationName)
      .then((p) => { if (!cancelled) setLocationPolicy(p); })
      .catch(() => { if (!cancelled) setLocationPolicy(null); });
    return () => { cancelled = true; };
  }, [car, pickupLocation]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-gray-400">
        <Loader2 className="animate-spin inline mr-2" size={18} /> Loading vehicle...
      </div>
    );
  }

  if (!car) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold">Vehicle not found.</h2>
        <button className="pill-btn primary mt-4" onClick={() => navigate("/cars")}>{t("detail.back")}</button>
      </div>
    );
  }

  const images = car.images && car.images.length > 0 ? car.images : (car.image ? [car.image] : []);
  // Prefer the car's own pickup location; fall back to the user-selected pickup
  const loc =
    locations.find((l) => l.name === car.pickupLocation) ||
    locations.find((l) => l.name === pickupLocation) ||
    locations[0] ||
    { tax: 0, minBookingDays: 1 };
  const minDays = loc.minBookingDays || 1;

  const onPickupDateChange = (val) => {
    const adjusted = ensureMinDays(val, dropoffDate, minDays);
    update({ pickupDate: val, dropoffDate: adjusted });
  };
  const dropoffMin = (() => {
    const d = new Date(pickupDate);
    if (isNaN(d.getTime())) return pickupDate;
    d.setDate(d.getDate() + minDays);
    return d.toISOString().slice(0, 10);
  })();
  const subtotal = car.price * days;
  const tax = +(subtotal * loc.tax / 100).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  const specs = [
    { icon: <Settings2 size={16} />, label: t("detail.transmission"), val: car.transmission || "Automatic" },
    { icon: <Fuel size={16} />, label: t("detail.fuel"), val: car.fuel || "Gasoline" },
    { icon: <Users size={16} />, label: t("detail.seats"), val: car.seats },
    { icon: <Briefcase size={16} />, label: t("detail.bags"), val: car.bags || 2 },
  ];

  const features = car.features && car.features.length > 0 ? car.features : ["Apple CarPlay", "Android Auto", "GPS", "Bluetooth", "USB ports"];

  const reviews = [
    { name: "Maria L.", rating: 5, text: "Spotless car, easy pickup at the airport. Will rent again!" },
    { name: "Carlos R.", rating: 5, text: "Great price and the team was super friendly. Smooth ride for our family trip." },
    { name: "Anna K.", rating: 4, text: "Loved the car. Just wish it had a bit more trunk space, but overall excellent." },
  ];

  const handleProceed = () => {
    update({ carId: car.id });
    navigate("/checkout");
  };

  const prevImg = () => setActiveImg((i) => (i - 1 + images.length) % images.length);
  const nextImg = () => setActiveImg((i) => (i + 1) % images.length);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => navigate(-1)} className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 mb-5">
        <ArrowLeft size={14} /> {t("detail.back")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        <div>
          {/* Gallery */}
          <div className="card-soft overflow-hidden">
            <div className="relative aspect-[16/10] bg-gray-100">
              {images.length > 0 ? (
                <img src={images[activeImg]} alt={car.name} className="w-full h-full object-cover" />
              ) : <div className="w-full h-full" />}
              {images.length > 1 && (
                <>
                  <button onClick={prevImg} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={nextImg} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors">
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="p-3 flex gap-2 overflow-x-auto">
                {images.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setActiveImg(i)}
                    className={`w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${i === activeImg ? "border-red-500" : "border-transparent"}`}
                  >
                    <img src={src} alt={`${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Header */}
          <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <p className="dams-brand-sub text-red-500 mb-1">{car.category}</p>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{car.name}</h1>
              <div className="mt-2 text-sm text-gray-600 inline-flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1 text-amber-500 font-semibold"><Star size={14} fill="currentColor" /><span className="text-gray-800">4.9</span> <span className="text-gray-500 font-normal">(124)</span></span>
                <span className="inline-flex items-center gap-1"><MapPin size={14} className="text-red-500" /> {car.location}</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-3xl font-extrabold">${car.price}</span>
              <span className="text-gray-500">{t("cars.perDay")}</span>
            </div>
          </div>

          {/* Specs */}
          <div className="mt-8">
            <h2 className="text-lg font-bold mb-3">{t("detail.specs")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {specs.map((s) => (
                <div key={s.label} className="card-soft p-4">
                  <div className="text-red-500 mb-1">{s.icon}</div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
                  <p className="font-semibold text-gray-900">{s.val}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="mt-8">
            <h2 className="text-lg font-bold mb-3">{t("detail.features")}</h2>
            {features.length === 0 ? (
              <p className="text-gray-500 text-sm">{t("detail.noFeatures")}</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-gray-700">
                    <Check size={14} className="text-emerald-600" /> {f}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mileage / Driver */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="card-soft p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{t("detail.mileage")}</p>
              <p className="font-bold">{car.unlimitedMileage !== false ? t("detail.unlimited") : `${car.mileageCap || 200} km/day`}</p>
            </div>
            <div className="card-soft p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{t("detail.minDriver")}</p>
              <p className="font-bold">{car.minDriverAge || 21} {t("detail.yearsOld")}</p>
            </div>
          </div>

          {/* Reviews */}
          <div className="mt-8">
            <h2 className="text-lg font-bold mb-3">{t("detail.reviews")}</h2>
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={`${r.name}-${r.text.slice(0, 20)}`} className="card-soft p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">{r.name}</span>
                    <span className="inline-flex items-center text-amber-500">
                      {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={12} fill="currentColor" />)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Booking summary card */}
        <aside>
          <div className="card-soft p-6 sticky top-24">
            <h3 className="font-bold text-lg mb-4">{t("detail.summary")}</h3>
            <div className="space-y-3 text-sm">
              <div>
                <label className="field-label flex items-center gap-1"><Calendar size={12} className="text-red-500" /> {t("search.pickupDate")}</label>
                <input type="date" className="input-soft" min={today} value={pickupDate} onChange={(e) => onPickupDateChange(e.target.value)} />
              </div>
              <div>
                <label className="field-label flex items-center gap-1"><Calendar size={12} className="text-red-500" /> {t("search.dropoffDate")}</label>
                <input type="date" className="input-soft" min={dropoffMin} value={dropoffDate} onChange={(e) => update({ dropoffDate: e.target.value })} />
                {minDays > 1 && (
                  <p className="text-[11px] text-gray-500 mt-1">Min {minDays} days at {loc.name}</p>
                )}
              </div>
              <div className="subtle-divider" />
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{t("detail.pricePerDay")}</span>
                <span className="font-semibold">${car.price}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{t("detail.days")}</span>
                <span className="font-semibold">{days}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{t("detail.subtotal")}</span>
                <span className="font-semibold">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{t("detail.tax")} ({loc.tax}%)</span>
                <span className="font-semibold">${tax.toFixed(2)}</span>
              </div>
              <div className="subtle-divider" />
              <div className="flex items-center justify-between text-base">
                <span className="font-bold">{t("detail.total")}</span>
                <span className="font-extrabold text-red-600 text-2xl">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Refundable security deposit + Mileage policy */}
            <DepositCard amount={car.deposit} variant="light" />
            <MileagePolicyCard car={car} policy={locationPolicy} days={days} />

            <button onClick={handleProceed} className="pill-btn primary w-full mt-5" style={{ padding: "0.85rem" }}>
              {t("detail.proceed")}
            </button>
            <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-3 justify-center">
              <Shield size={12} /> Free cancellation up to 48h before pick-up
            </p>

            <div className="subtle-divider mt-4" />
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Prefer to talk?</p>
            <div className="grid grid-cols-3 gap-2">
              <a
                href={`${CONTACT.whatsappLink}?text=${encodeURIComponent(`Hi! I'd like to rent the ${car.name} from ${pickupDate} to ${dropoffDate} (pickup: ${pickupLocation}).`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="pill-btn outline inline-flex items-center justify-center gap-1.5"
                style={{ padding: "0.6rem 0.5rem", fontSize: "0.8rem" }}
                title="WhatsApp"
              >
                <MessageCircle size={14} className="text-emerald-600" /> WhatsApp
              </a>
              <a
                href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(`Rental request — ${car.name}`)}&body=${encodeURIComponent(`Hi DAMS team,\n\nI'd like to rent the ${car.name}.\nPickup: ${pickupDate} at ${pickupLocation}\nDrop-off: ${dropoffDate}\n\nThanks!`)}`}
                className="pill-btn outline inline-flex items-center justify-center gap-1.5"
                style={{ padding: "0.6rem 0.5rem", fontSize: "0.8rem" }}
                title="Email"
              >
                <Mail size={14} className="text-red-500" /> Email
              </a>
              <a
                href={CONTACT.phoneLink}
                className="pill-btn outline inline-flex items-center justify-center gap-1.5"
                style={{ padding: "0.6rem 0.5rem", fontSize: "0.8rem" }}
                title="Phone"
              >
                <Phone size={14} className="text-blue-600" /> Call
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
